import React, { useState } from "react"
import { Button, Dropdown } from "antd"
import type { MenuProps } from "antd"
import { CheckOutlined, DownOutlined } from "@ant-design/icons"

export type MenuButtonItem =
  | {
      key: string
      label: string
      icon?: React.ReactNode
      onClick?: () => void
      checked?: boolean
      /** A keyboard shortcut shown muted after the label, e.g. "Shift + Alt + F". */
      shortcut?: string
      disabled?: boolean
      hidden?: boolean
    }
  | { type: "divider"; key: string }

interface MenuButtonProps {
  label: string
  items: MenuButtonItem[]
  primary?: boolean
  /** A small brand dot on the button, e.g. when something in the menu is on. */
  dot?: boolean
  icon?: React.ReactNode
  ariaLabel?: string
}

type AntMenuItem = NonNullable<MenuProps["items"]>[number]

const isDivider = (
  item: MenuButtonItem,
): item is { type: "divider"; key: string } => "type" in item

/**
 * Leaves hidden items out, then tidies the dividers: none first or last, and
 * a run of dividers (e.g. around a hidden item) collapses to one.
 */
const visibleItems = (items: MenuButtonItem[]): MenuButtonItem[] => {
  const kept: MenuButtonItem[] = []
  for (const item of items) {
    if (isDivider(item)) {
      const last = kept[kept.length - 1]
      if (last && !isDivider(last)) kept.push(item)
    } else if (!item.hidden) {
      kept.push(item)
    }
  }
  const last = kept[kept.length - 1]
  if (last && isDivider(last)) kept.pop()
  return kept
}

const toAntItem = (item: MenuButtonItem): AntMenuItem => {
  if (isDivider(item)) return { type: "divider", key: item.key }
  return {
    key: item.key,
    icon: item.icon,
    disabled: item.disabled,
    onClick: item.onClick,
    label: (
      <span className="dq-menu-button__item">
        <span>{item.label}</span>
        {item.shortcut && (
          <span className="dq-menu-button__shortcut">{item.shortcut}</span>
        )}
        {item.checked && (
          <CheckOutlined className="dq-menu-button__tick" aria-label="checked" />
        )}
      </span>
    ),
  }
}

/**
 * A "Name ▾" button: the themed antd button with a caret, opening a menu of
 * actions in the shared .dq-menu-popup look (styles/buttonTokens.css). Menus
 * are plain item lists, so other screens and branches just add entries.
 */
const MenuButton: React.FC<MenuButtonProps> = ({
  label,
  items,
  primary,
  dot,
  icon,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      menu={{
        items: visibleItems(items).map(toAntItem),
        className: "dq-menu-popup",
      }}
    >
      <Button
        type={primary ? "primary" : "default"}
        icon={icon}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className="dq-menu-button"
      >
        {label}
        <DownOutlined className="dq-menu-button__caret" aria-hidden="true" />
        {dot && <span className="dq-menu-button__dot" aria-hidden="true" />}
      </Button>
    </Dropdown>
  )
}

export default MenuButton
